import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Calendar range picker — klik na početni pa krajnji dan. UVIJEK dd.mm.yyyy prikaz (BI pravilo).
 * State je ISO yyyy-mm-dd; konverzija samo na UI granici. Bez `<input type="date">`.
 *
 * Popup se renderira u PORTALU (position:fixed, visok z-index) da ne bude skriven iza
 * susjednih kartica/overflow-a (bug m.1 filter retka).
 *
 *   <DateRangePicker fromIso={dateFrom} toIso={dateTo} onChange={(f, t) => { setFrom(f); setTo(t); }} locale={locale} />
 */
const HR_MONTHS = ['Siječanj', 'Veljača', 'Ožujak', 'Travanj', 'Svibanj', 'Lipanj', 'Srpanj', 'Kolovoz', 'Rujan', 'Listopad', 'Studeni', 'Prosinac'];
const EN_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const HR_DOW = ['Pon', 'Uto', 'Sri', 'Čet', 'Pet', 'Sub', 'Ned'];
const EN_DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const POPUP_W = 256;

function isoToDmy(iso: string): string {
    if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
    const [y, m, d] = iso.split('-');
    return `${d}.${m}.${y}`;
}
function ymd(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function DateRangePicker({
    fromIso, toIso, onChange, locale = 'hr',
}: {
    fromIso: string;
    toIso: string;
    onChange: (fromIso: string, toIso: string) => void;
    locale?: 'hr' | 'en';
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const months = locale === 'en' ? EN_MONTHS : HR_MONTHS;
    const dow = locale === 'en' ? EN_DOW : HR_DOW;
    const [open, setOpen] = useState(false);
    // Mjesec koji se prikazuje (početak na from ili danas)
    const [view, setView] = useState(() => {
        const base = fromIso && /^\d{4}-\d{2}-\d{2}$/.test(fromIso) ? new Date(fromIso) : new Date();
        return new Date(base.getFullYear(), base.getMonth(), 1);
    });
    const btnRef = useRef<HTMLButtonElement | null>(null);
    const popRef = useRef<HTMLDivElement | null>(null);
    const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

    // Izračunaj poziciju popupa iz pozicije gumba (viewport koordinate, position:fixed).
    const reposition = () => {
        const r = btnRef.current?.getBoundingClientRect();
        if (!r) return;
        const left = Math.max(8, Math.min(r.left, window.innerWidth - POPUP_W - 8));
        setCoords({ top: r.bottom + 4, left });
    };

    useLayoutEffect(() => {
        if (open) reposition();
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => {
            const tgt = e.target as Node;
            if (btnRef.current?.contains(tgt) || popRef.current?.contains(tgt)) return;
            setOpen(false);
        };
        const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
        const onScrollResize = () => reposition();
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onEsc);
        window.addEventListener('scroll', onScrollResize, true);
        window.addEventListener('resize', onScrollResize);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('keydown', onEsc);
            window.removeEventListener('scroll', onScrollResize, true);
            window.removeEventListener('resize', onScrollResize);
        };
    }, [open]);

    // Dani mjeseca (s leading praznim slotovima — tjedan počinje ponedjeljkom)
    const grid = useMemo(() => {
        const first = new Date(view.getFullYear(), view.getMonth(), 1);
        const startDow = (first.getDay() + 6) % 7; // 0 = ponedjeljak
        const daysInMonth = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
        const cells: (Date | null)[] = [];
        for (let i = 0; i < startDow; i++) cells.push(null);
        for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(view.getFullYear(), view.getMonth(), d));
        return cells;
    }, [view]);

    const pick = (d: Date) => {
        const iso = ymd(d);
        // Prazno ili oba postavljena → novi početak. Inače → kraj (ako je >= početak).
        if (!fromIso || (fromIso && toIso)) {
            onChange(iso, '');
        } else if (iso < fromIso) {
            onChange(iso, fromIso);
            setOpen(false);
        } else {
            onChange(fromIso, iso);
            setOpen(false);
        }
    };

    const inRange = (iso: string) => fromIso && toIso && iso >= fromIso && iso <= toIso;
    const todayIso = ymd(new Date());
    const label = fromIso ? `${isoToDmy(fromIso)} – ${toIso ? isoToDmy(toIso) : '…'}` : t('Odaberi raspon', 'Pick range');

    const popup = (
        <div ref={popRef} style={{ position: 'fixed', top: coords.top, left: coords.left, zIndex: 10000, background: '#fff', border: '1px solid #cbd5e1', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.16)', padding: 10, width: POPUP_W }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <button type="button" onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))} style={navBtn}>‹</button>
                <strong style={{ fontSize: '0.84rem' }}>{months[view.getMonth()]} {view.getFullYear()}</strong>
                <button type="button" onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))} style={navBtn}>›</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, fontSize: '0.66rem', color: '#888', textAlign: 'center', marginBottom: 2 }}>
                {dow.map((d) => <div key={d}>{d}</div>)}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
                {grid.map((d, i) => {
                    if (!d) return <div key={i} />;
                    const iso = ymd(d);
                    const isFrom = iso === fromIso, isTo = iso === toIso, within = inRange(iso);
                    const isToday = iso === todayIso;
                    const sel = isFrom || isTo;
                    return (
                        <button key={i} type="button" onClick={() => pick(d)}
                            style={{
                                padding: '5px 0', border: 0, borderRadius: 5, cursor: 'pointer', fontSize: '0.76rem',
                                background: sel ? 'linear-gradient(135deg, var(--ad-ink), var(--ad-acc))' : within ? 'rgba(var(--ad-acc-rgb),0.22)' : 'transparent',
                                color: sel ? '#fff' : '#333', fontWeight: sel || isToday ? 700 : 400,
                                outline: isToday && !sel ? '1px solid var(--ad-acc)' : 'none',
                            }}>
                            {d.getDate()}
                        </button>
                    );
                })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: '0.74rem' }}>
                <button type="button" onClick={() => { onChange('', ''); }} style={{ border: 0, background: 'transparent', color: '#b14040', cursor: 'pointer' }}>↺ {t('Očisti', 'Clear')}</button>
                <button type="button" onClick={() => setOpen(false)} style={{ border: 0, background: 'transparent', color: '#1f7a36', cursor: 'pointer', fontWeight: 700 }}>{t('Gotovo', 'Done')}</button>
            </div>
        </div>
    );

    return (
        <div style={{ position: 'relative' }}>
            <button ref={btnRef} type="button" onClick={() => setOpen((o) => !o)}
                style={{ width: '100%', padding: '5px 8px', textAlign: 'left', border: '1px solid rgba(var(--ad-soft-rgb),0.5)', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: '0.82rem' }}>
                📅 {label}
            </button>
            {open ? createPortal(popup, document.body) : null}
        </div>
    );
}

const navBtn: React.CSSProperties = { border: '1px solid rgba(var(--ad-soft-rgb),0.4)', background: '#fff', borderRadius: 5, cursor: 'pointer', padding: '2px 9px', fontSize: '0.9rem', lineHeight: 1 };
